"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { useHydrated } from "@/hooks/useHydrated";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";

/**
 * Renders a branded splash covering the app until React has finished
 * hydrating the provider tree, then fades out to reveal the UI.
 *
 * Why we hide children (not just overlay):
 *   While React is hydrating (3–5 s on slower devices because of the large
 *   provider tree — language, auth, channel, analytics, …), the main thread
 *   is busy running JS. Even with a non-blocking overlay, taps on the
 *   underlying UI look "queued but dead" because click handlers can't fire
 *   until hydration commits. Showing tappable-looking UI that cannot respond
 *   is worse UX than showing a clear loading splash.
 *
 * How it works:
 *   • SSR-safe: children are ALWAYS rendered into the DOM (crawlers and
 *     first-paint HTML contain real content). Only CSS `visibility` +
 *     `inert` are toggled.
 *   • `visibility: hidden` hides the subtree visually.
 *   • `inert` removes children from the focus/tab order, disables pointer
 *     events, and hides them from assistive technologies — so no accidental
 *     taps and no confusing "app content" announcements while the splash
 *     is up.
 *   • The splash is announced to screen readers via `role="status"` +
 *     `aria-live="polite"`. The visually-hidden status string starts
 *     EMPTY on the server and is populated once `hydrated` flips true.
 *     This post-mount DOM mutation is what actually triggers the
 *     live-region announcement — if the text were present from SSR,
 *     many screen readers (notably JAWS) would skip it, since polite
 *     regions only announce content changes, not initial content.
 *
 *     The announcement text reads "VidSeeds.ai ready", not "Loading",
 *     because it physically can only fire AFTER hydration completes
 *     (the main thread is blocked during hydration). Announcing
 *     "Loading" at the exact moment loading finishes is semantically
 *     backwards; "ready" matches what's true at the moment the SR hears
 *     it, and mirrors the sighted experience of the splash fading out.
 *
 *     Decorative visuals inside the splash carry `aria-hidden="true"`.
 *   • The splash overlay is `pointer-events-none`, so ghost taps during
 *     the splash fall through to nothing instead of hitting invisible UI.
 *   • When hydration commits, the splash fades out and children flip to
 *     visible + interactive in the same frame.
 */
export function HydrationGuard({ children }: { children: React.ReactNode }) {
  const hydrated = useHydrated();
  const { t } = useJsonTranslation("HydrationGuard");
  const [ready, setReady] = useState(false);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (!hydrated) return;
    let outerRaf = 0;
    let innerRaf = 0;
    outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(() => {
        setReady(true);
      });
    });
    return () => {
      cancelAnimationFrame(outerRaf);
      cancelAnimationFrame(innerRaf);
    };
  }, [hydrated]);

  // Unmount the splash only after the fade-out transition has completed.
  // Scheduling the timer in a separate effect means it starts AFTER React
  // commits `ready = true` (and therefore AFTER the CSS transition begins),
  // rather than before the commit. 350 ms = 300 ms CSS duration + ~50 ms
  // buffer for the browser to start the transition on the committed DOM.
  useEffect(() => {
    if (!ready) return;
    const fadeTimer = setTimeout(() => setVisible(false), 350);
    return () => clearTimeout(fadeTimer);
  }, [ready]);

  const isReady = hydrated && ready;

  return (
    <div className="relative">
      {visible && (
        <div
          data-hydration-splash
          role="status"
          aria-live="polite"
          className={`pointer-events-none fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-300 ${
            isReady ? "opacity-0" : "opacity-100"
          }`}
        >
          <span className="sr-only">
            {hydrated ? t("readyAnnouncement") : ""}
          </span>

          <div aria-hidden="true" className="contents">
            <div className="absolute inset-0 bg-[#0a0a0a]/80 backdrop-blur-sm" />

            <div className="relative flex flex-col items-center gap-4">
              <div className="animate-pulse">
                <Image
                  src="/logo-icon.png"
                  alt=""
                  width={48}
                  height={48}
                  className="h-12 w-auto"
                  priority
                />
              </div>

              <div className="text-lg font-semibold tracking-tight">
                <span className="text-leaf-600">Vid</span>
                <span className="text-gray-300">Seeds</span>
                <span className="text-green-500">.ai</span>
              </div>

              <div className="h-0.5 w-32 overflow-hidden rounded-full bg-white/10">
                <div className="via-leaf-500 h-full w-full origin-left animate-[shimmer_1.5s_ease-in-out_infinite] bg-gradient-to-r from-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        data-hydration-content
        className={isReady ? undefined : "invisible"}
        inert={!isReady}
      >
        {children}
      </div>
    </div>
  );
}
